/**
 * ports/account.ts
 *
 * Optional server accounts (username + password) and cross-device sync of
 * what this device keeps: progress, saved games, replays, and drafts.
 */

export interface Account {
  id: string;
  username: string;
  /** Their color in multiplayer (missing from sign-ins kept before accounts had one, until `refresh`). */
  color?: string;
  createdAt: number;
}

/** What a player can change about their account besides the password. */
export interface ProfileChanges {
  username?: string;
  color?: string;
}

/** What an account may do beyond playing. */
export interface AccountRoles {
  /** The admin page and API, with the account's sign-in. */
  admin: boolean;
  /** Other sites' puzzles listed in the catalog, fetched when opened. Admins have it too. */
  library: boolean;
}

export const NO_ROLES: AccountRoles = { admin: false, library: false };

export interface SignIn {
  account: Account;
  /** Sent as a bearer token; the server stores only its hash. */
  token: string;
}

export type SyncKind = 'progress' | 'session' | 'replay' | 'draft';

export interface SyncChange {
  kind: SyncKind;
  id: string;
  /** When the item last changed (epoch ms); the newest write wins. */
  updatedAt: number;
  deleted?: boolean;
  data?: unknown;
}

export interface SyncRequest {
  /** The cursor from the last sync (0 the first time). */
  since: number;
  changes: SyncChange[];
}

export interface SyncResponse {
  cursor: number;
  changes: SyncChange[];
  /** More changes are waiting; ask again with the new cursor. */
  more: boolean;
  rejected: Array<{ kind: SyncKind; id: string; reason: 'too large' | 'missing data' | 'account storage full' }>;
  /** The cursor is ahead of the server (restored from a backup): start again from 0. */
  reset?: boolean;
}

export interface AccountApiPort {
  register(username: string, password: string): Promise<SignIn>;
  login(username: string, password: string): Promise<SignIn>;
  logout(token: string): Promise<void>;
  logoutEverywhere(token: string): Promise<void>;
  me(token: string): Promise<{ account: Account; usage: { bytes: number; items: number } }>;
  roles(token: string): Promise<AccountRoles>;
  /** Changes the username (it must be free) and color; the account as it is now. */
  updateProfile(token: string, changes: ProfileChanges): Promise<{ account: Account }>;
  changePassword(token: string, current: string, next: string): Promise<void>;
  deleteAccount(token: string, password: string): Promise<void>;
  sync(token: string, request: SyncRequest): Promise<SyncResponse>;
}
